import { Grid, Typography, Skeleton, Link } from '@mui/material';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router';

interface LocationProfileDescriptionProps {
  desc?: string;
  loading?: boolean;
  /** Public supplier profile path (/supplier/{shortId}/{slug}); the "More yards" link renders only when set. */
  supplierProfileUrl?: string | null;
  /** Empty for anonymous visitors (the API masks it), so the link text falls back to a generic label. */
  supplierName?: string;
}

const DESC_TEXT_ID = 'ss-profile-desc-text';

const LocationProfileDescription = ({ desc, loading, supplierProfileUrl, supplierName }: LocationProfileDescriptionProps) => {
  const descRef = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  // True when the line clamp cuts the text off, so "Read more" only shows for long descriptions.
  const [clamped, setClamped] = useState(false);

  // Moving to another listing keeps this component mounted: start the new text collapsed and measure it again.
  const [shownDesc, setShownDesc] = useState(desc);
  if (desc !== shownDesc) {
    setShownDesc(desc);
    setExpanded(false);
    setClamped(false);
  }

  useLayoutEffect(() => {
    const el = descRef.current;
    if (!el || expanded) return;
    const measure = () => setClamped(el.scrollHeight > el.clientHeight);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [desc, expanded, loading]);

  // No text: skip the empty "Description" heading; only the supplier link (if any) is left.
  const hasDesc = !!desc?.trim();
  if (!loading && !hasDesc && !supplierProfileUrl) return null;

  return <Grid container direction='column' className='ss-profile-desc-container'>
    {loading ? <Skeleton width={"15%"} /> : hasDesc && <Typography className='ss-profile-desc-title' >Description</Typography>}
    {loading ? <Skeleton width={"70%"} /> : hasDesc && (
      <Typography
        ref={descRef}
        id={DESC_TEXT_ID}
        className={expanded ? 'ss-profile-desc' : 'ss-profile-desc ss-profile-desc-clamped'}
      >
        {desc}
      </Typography>
    )}
    {!loading && (clamped || supplierProfileUrl) && (
      <Typography component='div' className="ss-profile-desc ss-profile-desc-actions">
        {clamped && (
          <Link
            component='button'
            type='button'
            underline='none'
            sx={{ fontWeight: 500 }}
            aria-expanded={expanded}
            aria-controls={DESC_TEXT_ID}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? 'Show less' : 'Read more'}
          </Link>
        )}
        {supplierProfileUrl && (
          <Link component={RouterLink} to={supplierProfileUrl}>
            More yards from {supplierName || 'this supplier'}
          </Link>
        )}
      </Typography>
    )}
  </Grid>
}

export default LocationProfileDescription;
